import { useFormContext } from 'react-hook-form';
import type { Partnership } from '../types';
import { usePagination } from 'hooks/usePagination';
import { usePartnerships } from '../api';
import { usePartnershipsColumns } from '../hooks';
import { BaseGrid, SuspenseBoundary } from 'components/common';
import { memo, useCallback, useEffect, useState } from 'react';
import { useListParams } from 'hooks';
import { debounce } from 'lodash-es';
import { usePermissionGuard } from '../../../login';
import EditPartnershipAdTagDialog from './EditPartnershipAdTagDialog';

const PartnershipsGridComponent = memo(() => {
  const { accessToChangeFacebookpage } = usePermissionGuard();
  const { watch } = useFormContext();
  const pagination = usePagination([], 50);
  const { setQ, q: urlQ } = useListParams();
  const [editingPartnership, setEditingPartnership] = useState<Partnership | null>(null);

  const [debouncedFilter, setDebouncedFilter] = useState({
    q: urlQ ?? undefined,
  });

  const filterByQ = watch('q') as string;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdate = useCallback(
    debounce((q: string) => {
      setDebouncedFilter({ q });
      setQ(q ?? undefined);
    }, 500),
    [setQ],
  );

  useEffect(() => {
    debouncedUpdate(filterByQ);
    return () => {
      debouncedUpdate.cancel();
    };
  }, [filterByQ, debouncedUpdate]);

  const columns = usePartnershipsColumns({
    canEditAdTag: accessToChangeFacebookpage,
    onEditAdTag: (partnership) => {
      setEditingPartnership(partnership);
    },
  });

  const {
    data: { data: partnerships, count },
    isLoading,
  } = usePartnerships({
    params: {
      ...(debouncedFilter.q && { tag: debouncedFilter.q }),
      limit: pagination.perPage,
      offset: pagination.page,
    },
  });

  const handleCloseEditDialog = useCallback(() => {
    setEditingPartnership(null);
  }, []);

  return (
    <>
      <BaseGrid<Partnership>
        rows={partnerships}
        columns={columns}
        count={count}
        isLoading={isLoading}
        pagination={pagination}
        getRowId={(row) => row.id}
      />
      <EditPartnershipAdTagDialog
        partnership={editingPartnership}
        isOpen={Boolean(editingPartnership)}
        onClose={handleCloseEditDialog}
      />
    </>
  );
});

PartnershipsGridComponent.displayName = 'PartnershipsGridComponent';

const PartnershipsGrid = () => {
  return (
    <SuspenseBoundary>
      <PartnershipsGridComponent />
    </SuspenseBoundary>
  );
};

export default PartnershipsGrid;
